import { Injectable, NgZone } from '@angular/core';
import { MapState } from '../../state/map.state';
import { StateService } from '../../state/state.service';
import * as mapboxgl from 'mapbox-gl';
import MapboxClient from '@mapbox/mapbox-sdk';
import mbxGeocoder from '@mapbox/mapbox-sdk/services/geocoding';
import { MapElement } from '../map-element.model';
import { Point, GeometryObject, FeatureCollection, Geometry, Feature } from 'geojson';
import { MapService, MapStyle, MapMarker, MapBounds, MapLatLng, MarkerMode } from '../map.service';
import { Layer, HTMLMarkerLayer, CanvasLayer } from '../layer/layer.model';
import { ILayer } from '../layer/layer.type';
import { BehaviorSubject, combineLatest, Subject, ReplaySubject, Observable, timer, from } from 'rxjs';
import {
  map, filter, skip, distinctUntilChanged, debounceTime,
  mergeScan, scan, mergeMap, take, startWith, auditTime, switchMapTo, takeUntil, switchMap, withLatestFrom
} from 'rxjs/operators';
import { groupBy, _ } from '../../utils';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { MapPlace } from '../types';
import { SearchService, SearchResults } from '../../search.service';
import Measure from './utils/measure';
import { FlagService } from '../../flag/flag.service';
import { IFlag } from '../../flag/flag.model';
import { AuthService } from '../../../auth/auth.service';
import { EncryptedDataService } from '~app/auth/encrypted-data.service';
import { flatten, last, merge } from 'lodash-es';
import { getSharedLayer } from '../layer/layer-definitions/shared';
import { TranslateService } from '@ngx-translate/core';
import { LayerGroup } from '../layer/layer-group.model';
import { InfoActionService } from '~app/element-utils/element-actions/info-action/info-action.service';
import { AlarmService } from '~app/alarm/alarm.service';
import { AlarmMessage } from '~app/alarm/alarm-message';
import { LinkingCrudService } from '~app/linking/linking.service';
import { MappingService } from '~app/linking/mapping.service';
import { IRegoFeatureDefinition, IRegoMappingDefinition } from '~app/linking/mapping';
import { Geometry as TurfGeometry, center } from '@turf/turf';
import { NeveMeasurementsService } from '~app/custom/neve/measurements/neve-measurements.service';
import { TagPair } from '~app/shared/map/layer/layer-definitions/T14';
import { getFullTag } from '~app/custom/neve/measurements/neve-measurements-tag.service';
import { TagState } from '~app/custom/neve/measurements/neve-measurements.interface';

/**
 * Map styles
 */

const defaultStyles: MapStyle[] = [
  { name: _('Satellite'), url: 'mapbox://styles/mapbox/satellite-streets-v11' },
  { name: _('Streets'), url: 'mapbox://styles/mapbox/streets-v11' },
  { name: _('Light'), url: 'mapbox://styles/mapbox/light-v10' },
  { name: _('Dark'), url: 'mapbox://styles/mapbox/dark-v10' },
  { name: _('Outdoors'), url: 'mapbox://styles/mapbox/outdoors-v11' },
];

const tenantSpecificStyles: { [key: number]: MapStyle[] } = {
  8: [
    { name: _('Orthophotos'), url: 'mapbox://styles/mipro-dev/ckcyhsakz1um51impmsijrcw9' }
  ]
};

@Injectable()
export class MapboxService implements MapService {

  private map: mapboxgl.Map;
  private geocoder: mbxGeocoder;
  private accessToken = environment.mapboxToken;

  // populated in constructor
  private availableStyles: MapStyle[] = [];

  /**
   * Latest state (like zoom, center, visible layers etc) saved to local storage
   * to store them between app refreshes
   */
  private mapState: MapState;

  /**
   * Layers from tenant based layerDefinitions
   */
  private readonly layersBehaviorSubject = new BehaviorSubject<Layer[]>([]);
  public readonly layers$ = this.layersBehaviorSubject.asObservable();

  private readonly layersReplaySubject = new ReplaySubject<Layer>();
  public readonly layersR$ = this.layersReplaySubject.asObservable();

  /**
   * Tenant's layer groups
   */
  private readonly layerGroupsSubject = new BehaviorSubject<LayerGroup[]>([]);
  public readonly layerGroups$ = this.layerGroupsSubject.asObservable();

  private readonly layerGroupsReplaySubject = new ReplaySubject<LayerGroup>();
  public readonly layerGroupsR$ = this.layerGroupsReplaySubject.asObservable();

  /**
   * When subscribed, the observable will emit every loaded layer until now and
   * rest of the layers when their loads finish
   */
  public readonly loadedLayers$: Observable<Layer> = this.layersR$
    .pipe(
      mergeMap(layer => layer.isLoaded$.pipe(
        filter(isLoaded => isLoaded),
        map(() => layer),
        take(1)
      ))
    );

  public readonly notLoadedLayers$: Observable<Set<Layer>> = this.layersR$
    .pipe(
      mergeScan((acc, layer) => layer.isLoaded$.pipe(
        map(isLoaded => {
          if (isLoaded) {
            acc.delete(layer);
          } else {
            acc.add(layer);
          }
          return acc;
        })
      ), new Set<Layer>())
    );

  public readonly visibleLayers$: Observable<{ [key: string]: boolean }> = this.layersR$
    .pipe(mergeScan((acc: { [key: string]: boolean }, layer) => layer.isVisible$.pipe(
      map(isVisible => {
        acc[layer.id] = isVisible;
        return acc;
      }),
    ), <{ [key: string]: boolean }>{}));

  public readonly selectedLayers$: Observable<{ [key: string]: boolean }> = this.layersR$
    .pipe(mergeScan((acc: { [key: string]: boolean }, layer) => layer.isSelected$.pipe(
      map(isSelected => {
        acc[layer.id] = isSelected;
        return acc;
      }),
    ), <{ [key: string]: boolean }>{}));

  public readonly visibleLayerGroups$: Observable<{ [key: string]: boolean }> = this.layerGroupsR$
    .pipe(mergeScan((acc: { [key: string]: boolean }, group) => group.isVisible$.pipe(
      map(isVisible => {
        acc[group.name] = isVisible;
        return acc;
      }),
    ), <{ [key: string]: boolean }>{}));

  private readonly geojsonMap: Map<string, MapElement<Geometry>[]> = new Map();

  private readonly markerObservablesRS = new ReplaySubject<Observable<MapElement<Point>[]>>();

  public readonly allMarkers$ = this.markerObservablesRS
    .pipe(
      scan((acc: Observable<MapElement<Point>[]>[], m$) => [...acc, m$], []),
      mergeMap(markerObservables =>
        combineLatest(markerObservables).pipe(map(flatten)),
      )
    );

  public readonly visibleMarkers$ = combineLatest([this.allMarkers$, this.visibleLayers$])
    .pipe(map(([markers, visibleLayers]) => {
      return markers.filter(m => visibleLayers[m.layer.id] === true);
    })
    );

  private readonly currentFeatureSubject = new BehaviorSubject<MapElement<GeometryObject> | null>(null);
  /**
   * Currently selected feature
   */
  public readonly currentFeature$ = this.currentFeatureSubject.asObservable();

  private readonly hoverFeatureSubject = new Subject<{ data: MapElement<Point>, event: any }>();
  /**
   * Currently hovered feature
   */
  public readonly hoverFeature$ = this.hoverFeatureSubject.asObservable();

  /**
   * Mapbox styledata changed, emits the new id
   */
  private readonly styleDataChangedBS = new BehaviorSubject<string>(null);
  private readonly styleDataChanged$ = this.styleDataChangedBS.pipe(distinctUntilChanged());

  /**
   * Map's center changed
   */
  private readonly centerBehaviorSubject = new BehaviorSubject(null);
  public readonly center$ = this.centerBehaviorSubject.pipe(
    skip(1),
    debounceTime(1000 / 30),
  );

  /**
   * Mapbox zoom event
   */
  private readonly zoom = new BehaviorSubject<number>(null);
  public readonly zoom$ = this.zoom.pipe(
    debounceTime(1000 / 30),
    map(Math.floor),       // Emit only whole number
    distinctUntilChanged() // when it changes
  );

  private readonly moveEndSubject = new Subject<void>();
  public readonly moveEnd$ = this.moveEndSubject.asObservable();
  private readonly zoomEndSubject = new Subject<void>();
  public readonly zoomEnd$ = this.zoomEndSubject.asObservable();
  private readonly sourceDataSubject = new Subject<mapboxgl.MapSourceDataEvent>();
  private readonly sourceData$ = this.sourceDataSubject.asObservable();

  public readonly searchResults$: Observable<SearchResults> = this.layersR$
    .pipe(
      filter(layer => layer.searchOptions.searchable),
      mergeMap(layer =>
        combineLatest([
          this.searchService.searchTermChanged$.pipe(filter((searchTerm) => searchTerm?.length > 0)),
          layer.features$,
          layer instanceof CanvasLayer && layer.linkable
            ? this.mappingService.getRegoMappingList(layer.id).catch(err => undefined)
            : from([undefined] as IRegoMappingDefinition[]),
        ]).pipe(
          map(([searchTerm, _features, mappings]) => {
            const results = layer.searchFromFeatures(searchTerm, mappings?.layers?.[0]?.features);
            console.log(`map search results for ${searchTerm}`, results);
            return {
              sourceId: 'map_' + layer.id,
              sourceName: layer.name,
              results: results
                .map(layerSearchResult => ({
                  ...layerSearchResult,
                  sourceId: 'map_' + layer.id,
                  sourceName: layer.name,
                }))
            };
          }),
        )
      )
    );

  /**
   * Map measurements
   */
  public measure: Measure;

  private readonly markerModeBS = new BehaviorSubject(MarkerMode.HTML);
  public readonly markerMode$ = this.markerModeBS.asObservable();

  private hasMultipleMarkerModes: boolean;

  constructor(
    private readonly stateService: StateService,
    private readonly http: HttpClient,
    private readonly searchService: SearchService,
    private readonly flagService: FlagService,
    private readonly ngZone: NgZone,
    private readonly encryptedData: EncryptedDataService,
    private readonly authService: AuthService,
    private readonly translate: TranslateService,
    private readonly infoActionService: InfoActionService,
    private readonly alarmService: AlarmService,
    private readonly linkingService: LinkingCrudService,
    private readonly mappingService: MappingService,
    private readonly neveMeasurements: NeveMeasurementsService,
  ) {
    // Get the state saved in local storage
    this.mapState = this.stateService.getPartOfState('mapState');

    // Map styles
    this.availableStyles = [
      ...defaultStyles,
      ...(tenantSpecificStyles[this.authService.getTenant()] || [])
    ];

    // Saving map state
    combineLatest([
      this.visibleLayers$,
      this.selectedLayers$,
      this.visibleLayerGroups$,
      this.styleDataChanged$,
      this.moveEnd$.pipe(startWith(null)),
      this.zoomEnd$.pipe(startWith(null)),
    ])
      .pipe(auditTime(1000)) // Don't save more often than 1 time per second
      .subscribe(([layersVisibility, selectedLayers, layerGroupsVisibility]) => {
        this.mapState = {
          ...this.mapState,
          layersVisibility: { ...this.mapState.layersVisibility, ...layersVisibility },
          selectedLayers: { ...this.mapState.selectedLayers, ...selectedLayers },
          layerGroupsVisibility: { ...this.mapState.layerGroupsVisibility, ...layerGroupsVisibility },
          center: this.getCenter(),
          zoom: this.getZoom()
        };
        console.log('Save map state', this.mapState);
        this.stateService.updateState('mapState', this.mapState);
      });

    // Handle CanvasLayer hovers: show hover cursor if any of them is currently being hovered
    this.layers$.pipe(
      switchMap(
        layers => combineLatest(
          layers
            .filter(layer => layer instanceof CanvasLayer)
            .map((layer: CanvasLayer) => layer.isHovered$)
        )
      ),
    ).subscribe(isHovereds => {
      this.map.getCanvas().style.cursor = isHovereds.includes(true) ? 'pointer' : '';
    });

    // Publish search results
    this.searchResults$.subscribe(results => {
      this.searchService.publishResult(results);
    });
  }

  /**
   * Must be run before the map can be used
   */
  public async initMap(element: HTMLElement) {
    if (this.map) {
      console.warn('Trying to initMap multiple times!');
      return;
    }

    // Get map style from saved state, fall back to default if it's not valid
    if (!this.mapState.style || !this.mapState.style.url || !this.availableStyles.some(style => style.url === this.mapState.style.url)) {
      this.mapState.style = this.availableStyles[2];
    }

    if (this.mapState.markerMode) {
      this.markerModeBS.next(this.mapState.markerMode);
    } else {
      this.mapState.markerMode = this.markerModeBS.getValue();
    }

    this.map = this.createMapboxInstance(element);
    globalThis.mapbox = this.map; // for easier debugging

    this.measure = new Measure(this.map);

    /**
     * Add layers depending on tenant
     */
    const currentTenantConfig = this.authService.getTenantConfig(this.mapState.markerMode);

    const tenantLayers = currentTenantConfig.layerDefinitions;
    this.hasMultipleMarkerModes = currentTenantConfig.hasMultipleMarkerModes;

    await this.addImages();

    this.encryptedData.data$
      .subscribe(async encryptedData => {
          // Add tenant layers
          for (let layerDefinition of tenantLayers) {
            layerDefinition = merge(
              {},
              currentTenantConfig.layerDefinitionDefaults || {},
              encryptedData.map?.layerDefinitionDefaults || {},
              layerDefinition,
              encryptedData.map?.layerDefinitions?.[layerDefinition.id] || {},
            );
            if (layerDefinition.geojsonURL) {
              // resolve relative URLs against the configured GeoJSON location
              layerDefinition.geojsonURL = new URL(layerDefinition.geojsonURL, environment.geojsonURL).href;
            }
            try {
              await this.addLayer(layerDefinition);
            } catch (err) {
              console.error(err);
            }
            // Wait 5ms between adding layers so that mapbox has time to finish adding layer
            // This is needed for sorting the layers
            await timer(5).toPromise();
          }
        // Flag layer is used regardless of tenant
        this.setupFlagLayer();
        this.setupSelectionLayer();
        this.setupLinkedAlarms(tenantLayers.filter(layer => layer.linkable));
        this.setupAnimationUpdates(tenantLayers.filter(layer => layer.animated));
      });

    this.geocoder = mbxGeocoder(
      MapboxClient({
        accessToken: this.accessToken,
      })
    );

    /**
     * Add event listeners outside angular zone
     */
    this.ngZone.runOutsideAngular(() => {
      // Map layers can be drawn after getting 'styledata'
      // Styledata fires every time when the map style is loaded or changed(loading layer changes style data)
      this.map.on('styledata', ev => {
        this.styleDataChangedBS.next(ev.style.stylesheet.id);
      });

      this.map.on('move', () => {
        const newCenter = this.getCenter();
        this.centerBehaviorSubject.next(newCenter);
      });

      this.zoom.next(this.getZoom()); // Emit initial zoom level
      this.map.on('zoom', event => {
        const newZoom = this.getZoom();
        this.zoom.next(newZoom);
      });

      this.map.on('moveend', () => this.moveEndSubject.next());
      this.map.on('zoomend', () => this.zoomEndSubject.next());

      this.map.on('sourcedata', event => this.sourceDataSubject.next(event));

      this.map.on('click', () => {
        // no layer handled the event, so the click wasn't on any feature
        this.changeCurrentFeature(null);
      });
    });
  }

  private createMapboxInstance(element: HTMLElement) {
    const currentTenantConfig = this.authService.getTenantConfig(this.mapState.markerMode);

    const map = new mapboxgl.Map({
      accessToken: this.accessToken || null,
      container: element || null,
      style: this.mapState.style.url || null,
      zoom: this.mapState.zoom || currentTenantConfig.defaultZoom || null,
      center: this.mapState.center || currentTenantConfig.defaultCenter || null,
      dragRotate: false,
      pitchWithRotate: false,
      logoPosition: 'bottom-right',
    });
    map.addControl(
      // https://docs.mapbox.com/mapbox-gl-js/api/markers/#geolocatecontrol
      new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
          // don't use cached position, to avoid misleading the user
          maximumAge: 0,
        },
        trackUserLocation: true,
        showAccuracyCircle: true,
        showUserLocation: true,
      }),
      'bottom-right',
    );
    return map;
  }

  private async setupFlagLayer() {
    const flags = getSharedLayer('flags');
    if (flags.style.layerType === 'symbol') {
      const flagLayer: CanvasLayer = await this.addLayer(flags) as CanvasLayer;
      combineLatest([this.flagService.flags$, this.layers$, this.loadedLayers$])
        .pipe(
          map(([flags, layers]) => {
            const visibleLayers = layers
              .filter(layer => layer.style.visible && !layer.asMarkers)
              .map(layer => layer.id);
            const visibleFlags = flags.filter(flag => visibleLayers.includes(flag.layerId));
            return visibleFlags;
          })
        )
        .subscribe(flags => {
          const flagsAsFeatures: Feature<Geometry, IFlag>[] = flags.map(flag => {
            return {
              id: flag.targetObjectId,
              type: 'Feature',
              properties: { ...flag },
              geometry: flag.geometry
            };
          });
          const dataAsFeatureCollection: FeatureCollection<Geometry> = {
            type: 'FeatureCollection',
            features: flagsAsFeatures.map((flag) => ({
              id: flag.id,
              type: 'Feature',
              properties: {
                ...flag.properties,
                id: flag.id,
                label: flag.properties.targetObjectId
              },
              geometry: flag.geometry
            }))
          };
          flagLayer.pushGeojson(dataAsFeatureCollection);
        });
    }
  }

  private async setupSelectionLayer() {
    const layerSpec = getSharedLayer('selection');
    const selectionLayer = (await this.addLayer(layerSpec)) as CanvasLayer;
    this.currentFeature$.subscribe(feature => {
      if (feature?.layer instanceof CanvasLayer) {
        selectionLayer.pushGeojson({
          type: 'FeatureCollection',
          // feature is constructed anew because the incoming feature might not be valid
          features: [center({
            type: 'Feature',
            geometry: feature.geometry as TurfGeometry,
            properties: {},
          })],
        });
        selectionLayer.moveBelow(feature.layer);
        selectionLayer.setVisibility(true);
      } else {
        selectionLayer.setVisibility(false);
      }
    });
  }

  /**
   * Automatically push alarms into feature-state
   */
  private setupLinkedAlarms(layers: ILayer[]) {
    // Updates the alarm related feature-states for all features on the given layer
    const updateSource = async (layer: ILayer, alarms: AlarmMessage[]): Promise<void> => {
      // Collecting alarms into a Map reduces the number of times the list needs to be iterated and IDs resolved.
      const alarmsByFeatureId = new Map<string, AlarmMessage[]>();
      for (const alarm of alarms) {
        for (const linking of await this.linkingService.getLinkingsForSourceId(AlarmService.getIdentifier(alarm.metadata))) {
          if (alarmsByFeatureId.has(linking.regoId)) {
            alarmsByFeatureId.get(linking.regoId).push(alarm);
          } else {
            alarmsByFeatureId.set(linking.regoId, [alarm]);
          }
        }
      }

      // `removeFeatureState` can only remove the entire state object when not given a feature ID,
      // so we have to just overwrite the alarm related states for all loaded features...
      for (const feature of this.map.querySourceFeatures(layer.id, {
        sourceLayer: layer.vectorSourceLayer,
      })) {
        const featureAlarms = alarmsByFeatureId.get(feature.id.toString()) ?? [];
        this.map.setFeatureState({
          id: feature.id,
          source: layer.id,
          sourceLayer: layer.vectorSourceLayer,
        }, {
          'has-alarms': featureAlarms.length > 0,
          'alarm-count': featureAlarms.length,
        });
      }
    };

    // update all layers when alarms change
    this.alarmService.alarmsActiveForUser$.subscribe(alarms => {
      for (const layer of layers) {
        updateSource(layer, alarms);
      }
    });

    // update specific layers when tiles for them load
    this.sourceData$.pipe(
      withLatestFrom(this.alarmService.alarmsActiveForUser$),
    ).subscribe(([event, alarms]) => {
      if (event.isSourceLoaded) {
        for (const layer of layers) {
          if (layer.id === event.sourceId) {
            updateSource(layer, alarms);
          }
        }
      }
    });
  }

  /**
   * Provides a rolling timestamp feature-state for Mapbox based animations
   */
  private setupAnimationUpdates(layers: ILayer[]) {
    // The animation doesn't have to be super smooth, so using an interval instead of `requestAnimationFrame` to save CPU cycles.
    // Due to the convoluted animation pipeline, frequent updates slow down the entire app.
    this.ngZone.runOutsideAngular(() => {
      setInterval(() => {
        const timestamp = Date.now();
        for (const layer of layers) {
          for (const feature of this.map.querySourceFeatures(layer.id, {
            sourceLayer: layer.vectorSourceLayer,
          })) {
            if (feature.id) {
              this.map.setFeatureState({
                id: feature.id,
                source: layer.id,
                sourceLayer: layer.vectorSourceLayer,
              }, {
                timestamp,
              });
            }
          }
        }
      }, 1000 / 24); // 24 fps for that CINEMATIC look
    });
  }

  /**
   * Fetch all layers from map server
   */
  public getLayersFromBackend(): Promise<ILayer[]> {
    return this.http.get<ILayer[]>(environment.apiURL + 'layers').toPromise();
  }

  public async addLayer(layerDefinition: ILayer): Promise<Layer> {
    if (this.getLayerList().find(l => l.id === layerDefinition.id)) {
      console.warn('Trying to add layer', layerDefinition.id, 'multiple times');
      return;
    }

    const layerGroup = this.getLayerGroup(layerDefinition);
    const geojson = layerDefinition.geojsonURL
      ? await this.loadGeoJsonFromUrl(layerDefinition.geojsonURL)
      : null;
    let layerVisibility = !!layerDefinition.style.visible;
    // Read layer's previous visibility from local storage and use it
    if (this.mapState.layersVisibility) {
      layerVisibility = this.mapState.layersVisibility[layerDefinition.id] !== undefined
        ? this.mapState.layersVisibility[layerDefinition.id]
        : layerDefinition.style.visible;
    }

    const layer: Layer = layerDefinition.style.layerType === 'html-marker'
      ? this.createMarkerLayer(layerDefinition, geojson, layerGroup, layerVisibility)
      : await this.createCanvasLayer(layerDefinition, geojson, layerGroup, layerVisibility);

    // Read layer's selectection state from local storage and use it
    if (layerGroup && this.mapState.selectedLayers) {
      layer.setIsSelected(this.mapState.selectedLayers[layerDefinition.id] !== undefined
        ? this.mapState.selectedLayers[layerDefinition.id]
        : true);
    }

    // Emit layers
    const layers = this.layersBehaviorSubject.getValue();
    layers.push(layer);
    this.layersBehaviorSubject.next(layers);

    // Emit layers with RS
    this.layersReplaySubject.next(layer);

    return layer;
  }

  private getLayerGroup(layerDefinition: ILayer) {
    let layerGroup: LayerGroup;
    if (layerDefinition.metadata?.group) {
      layerGroup = this.getLayerGroupList().find(group => group.name === layerDefinition.metadata.group);
      if (layerGroup) {
        return layerGroup;
      } else {
        layerGroup = new LayerGroup(layerDefinition.metadata.group, false);
      }
      // Read layer group's previous visibility from local storage and use it
      if (this.mapState.layerGroupsVisibility) {
        const savedVisibility = this.mapState.layerGroupsVisibility[layerGroup.name] !== undefined
          ? this.mapState.layerGroupsVisibility[layerGroup.name]
          : true;

        // Show group if it was previously open
        layerGroup.setVisibility(savedVisibility);
      } else {
        layerGroup.setVisibility(true);
      }
      const groups = this.getLayerGroupList();
      groups.push(layerGroup);
      this.layerGroupsSubject.next(groups);
      this.layerGroupsReplaySubject.next(layerGroup);
    }
    return layerGroup;
  }

  private createMarkerLayer(
    layerDefinition: ILayer,
    geojson: FeatureCollection<Geometry>,
    group: LayerGroup,
    visibility: boolean): HTMLMarkerLayer {

    const htmlLayer = new HTMLMarkerLayer(this.map, layerDefinition);
    if (group) {
      group.addLayer(htmlLayer);
      htmlLayer.setLayerGroup(group);
      htmlLayer.setIsSelected(visibility);
    } else {
      htmlLayer.setVisibility(visibility);
    }
    // Get marker streams from layers
    this.markerObservablesRS.next(htmlLayer.features$);
    this.styleDataChanged$
      .pipe(
        filter(styleId => !!styleId),
        switchMapTo(this.encryptedData.data$)
      )
      .subscribe(async () => {
        await this.addImages();
        await htmlLayer.initLayer(geojson);
      });
    return htmlLayer;
  }

  private async createCanvasLayer(
    layerDefinition: ILayer,
    geojson: FeatureCollection,
    group: LayerGroup,
    visibility: boolean,
  ): Promise<Layer> {
    const canvasLayer = new CanvasLayer(this.map, layerDefinition, this.translate);
    if (group) {
      group.addLayer(canvasLayer);
      canvasLayer.setLayerGroup(group);
      canvasLayer.setIsSelected(visibility);
    } else {
      canvasLayer.setVisibility(visibility);
    }

    // wait for everything to load before continuing
    await this.styleDataChanged$.pipe(
      filter(styleId => !!styleId),
      switchMapTo(this.encryptedData.data$),
      take(1),
    ).toPromise();

    await this.addImages();
    await canvasLayer.initLayer(this.getBeforeLayerId(canvasLayer), geojson);
    this.setupClickHandler(canvasLayer);
    if (layerDefinition.linkable && layerDefinition.linkedMeasurements?.length > 0) {
      this.setupLinkedMeasurements(canvasLayer, layerDefinition);
    }

    return canvasLayer;
  }

  private setupClickHandler(canvasLayer: CanvasLayer): void {
    canvasLayer.featureClicked$
      .pipe(
        takeUntil(canvasLayer.clicksUnsubscribe),
        map(clickData => {
          const layerId = clickData.clickedFeature?.layer;
          const layer = this.getLayerList().find(layer => layer.id === layerId);
          return {
            ...clickData,
            clickedFeature: {
              ...clickData.clickedFeature,
              layer,
            }
          };
        })
      )
      .subscribe(clickData => {
        switch (clickData.event.type) {
          case 'click':
            this.changeHoverFeature({
              data: clickData.clickedFeature,
              event: clickData.event,
            });
            break;
          case 'dblclick':
            if (clickData.clickedFeature.layer.metadata.component) {
              // no call to `changeCurrentFeature` needed here, because 'click' handler always fires before 'dblclick'
              this.infoActionService.openDetails(clickData.clickedFeature.layer, clickData.clickedFeature);
              this.changeHoverFeature(null);
            }
            break;
          default:
            console.warn('Unhandled click event type', clickData);
            break;
        }
      });
  }

  private async setupLinkedMeasurements(canvasLayer: CanvasLayer, layerDefinition: ILayer): Promise<void> {
    // resolve linkings/mappings beforehand, both for performance and to filter out unlinked tags
    const tags: {
      tag: TagPair,
      feature: IRegoFeatureDefinition
    }[] = [];
    for (const tag of layerDefinition.linkedMeasurements) {
      for (const linking of await this.linkingService.getLinkingsForSourceId(getFullTag(tag))) {
        const feature = await this.mappingService.getRegoFeatureDefinition(linking.regoId, layerDefinition.id);
        if (feature) {
          tags.push({ tag, feature });
        }
      }
    }

    this.neveMeasurements.init();

    const formatter = layerDefinition.linkedMeasurementFormatter
      ?? ((measurements) => measurements.map(({label, value, unit}) => `${label}: ${value} ${unit}`).join('\n'));

    const texts$: Observable<{
      feature: IRegoFeatureDefinition,
      minValue: number,
      maxValue: number,
      text: string,
    }[]> = combineLatest(
      tags.map(({ tag, feature }) => this.neveMeasurements.tagState$(tag).pipe(
        // start with a null so that combineLatest doesn't have to wait for all tags to complete loading
        startWith(null as TagState),
        map(state => ({ feature, state })),
      ))
    ).pipe(
      map(entries => {
        return Array.from(groupBy(
          entries.filter(({ state }) => state?.status === 'loaded' && state?.measurements?.length > 0),
          entry => entry.feature,
          entry => entry.state,
        ).entries()).map(([feature, states]) => {
          return {
            feature,
            minValue: Math.min(...states.map(state => parseFloat(last(state.measurements).value))),
            maxValue: Math.max(...states.map(state => parseFloat(last(state.measurements).value))),
            text: formatter(states.map(state => ({
              label: state.metadata.tag.Description,
              value: last(state.measurements).value,
              unit: state.metadata.tag.Unit,
            }))),
          };
        });
      }),
      filter(entries => entries.length > 0),
    );

    if (canvasLayer.geojsonURL && canvasLayer.isPointLayer()) {
      texts$.subscribe(entries => {
        canvasLayer.setFeatureProperties(
          Object.fromEntries(
            entries.map(({ feature, maxValue, text }) => [feature.id, { maxValue, text }])
          )
        );
      });
    } else {
      texts$.pipe(
        map(entries => {
          return entries.map<Feature>(({ feature, text }) => ({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: feature.center,
            },
            id: feature.id,
            properties: { text },
          }));
        }),
      ).subscribe(
        await canvasLayer.addUtilityLayer('linkedMeasurements', {
          style: {
            layerType: 'symbol',
            icon: {
              textField: ['get', 'text'],
            },
          },
          mapboxOptions: {
            layout: {
              'text-radial-offset': 1,
              'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
              'text-max-width': 100, // prevent wrapping
            }
          }
        })
      );
    }
  }

  private getBeforeLayerId(layer: CanvasLayer): string {
    if (layer.belowRoadLayers) {
      // map labels are on the first symbol layer
      return this.map.getStyle().layers?.find(curLayer => curLayer.type === 'symbol')?.id;
    } else if (!(layer.belowTitles === undefined) && !(layer.belowTitles === null) && layer.belowTitles) {
      return null;
    } else {
      const layerList = this.getLayerList();
      const filteredLayers = layerList
        .filter(curLayer => !(curLayer instanceof HTMLMarkerLayer)    // HTMLMarkerLayers cannot be put below CanvasLayers
          && !curLayer.belowRoadLayers            // Sort only if not belowRoadLayers
          && !!this.map.getLayer(curLayer.id));   // Sort only layers, which are already added in mapbox
      const sortedLayers = [...filteredLayers, layer].sort((a, b) => (b.orderingWeight || 0) - (a.orderingWeight || 0));
      const indexL = sortedLayers.find(sortedLayer => sortedLayer.id === layer.id);
      const index = sortedLayers.indexOf(indexL);
      const beforeLayer = sortedLayers[index + 1];
      return beforeLayer?.id;
    }
  }

  private addImages() {
    const currentTenantConfig = this.authService.getTenantConfig(this.mapState.markerMode);
    const tenantImages = currentTenantConfig.images || [];
    return Promise.all(tenantImages.map(async i => {
      return await new Promise((resolve, reject) => {
        this.map.loadImage(
          i.url,
          (error, image) => {
            if (error) {
              reject(error);
            } else if (!this.map.hasImage(i.id)) {
              this.map.addImage(i.id, image);
            }
            resolve(i.id);
          }
        );
      });
    }));
  }

  async loadGeoJsonFromUrl(geojsonURL: string): Promise<FeatureCollection<Geometry>> {
    const rawGeoJson = await this.http.get<FeatureCollection<Geometry>>(geojsonURL).toPromise();
    const filteredFeatures = rawGeoJson.features.map(f => {
      if (f.geometry.type === 'GeometryCollection') {
        f.geometry.geometries = f.geometry.geometries.filter(geometry => geometry.type !== 'Polygon');
      }
      return f;
    });
    const filteredGeoJson = {
      ...rawGeoJson,
      features: filteredFeatures
    };
    return filteredGeoJson;
  }


  public createNewMarker(
    latLng: { lat: number, lng: number },
    markerElement?: HTMLElement,
    offset?: mapboxgl.PointLike,
  ): MapMarker {
    const marker = new mapboxgl.Marker(markerElement)
      .setLngLat(latLng)
      .addTo(this.map);
    if (offset) {
      marker.setOffset(offset);
    }
    return marker;
  }

  public getLayerFeatures(): Map<string, MapElement<Geometry>[]> {
    const featuresInLayers = new Map([
      // Removed markerMap
      ...Array.from(this.geojsonMap)
    ]);
    return featuresInLayers;
  }

  public getLayerList(): Layer[] {
    return this.layersBehaviorSubject.getValue();
  }

  public getLayerGroupList(): LayerGroup[] {
    return this.layerGroupsSubject.getValue();
  }

  public setLayerVisibility(layerId: string, visibility: boolean): void {
    /**
     * Set geojson layer visibility
     */
    const mapHasLayer = this.map.getLayer(layerId) !== undefined;
    if (mapHasLayer) {
      this.map.setLayoutProperty(layerId, 'visibility', visibility ? 'visible' : 'none');
    }
    /**
     * Changing marker layers' visibility is done updating layers$
     */
    const layers = this.layersBehaviorSubject.getValue();
    this.layersBehaviorSubject.next(layers.map(layer => {
      if (layer.id === layerId) {
        layer.style.visible = visibility;
      }
      return layer;
    }));
  }

  public changeCurrentFeature(feature: MapElement<GeometryObject> | null) {
    const previous = this.currentFeatureSubject.getValue();
    if (previous?.layer?.id && this.map.getLayer(previous.layer.id)) {
      this.map.setFeatureState({
        source: previous.layer.id,
        sourceLayer: previous.layer instanceof CanvasLayer ? previous.layer.vectorSourceLayer : undefined,
        id: previous.id,
      }, {
        selected: false,
      });
    }
    if (feature?.layer?.id && this.map.getLayer(feature.layer.id)) {
      this.map.setFeatureState({
        source: feature.layer.id,
        sourceLayer: feature.layer instanceof CanvasLayer ? feature.layer.vectorSourceLayer : undefined,
        id: feature.id,
      }, {
        selected: true,
      });
    }
    this.currentFeatureSubject.next(feature);
  }

  public changeHoverFeature(data: { data: MapElement<Point>, event: any }) {
    const hoverFeature = data ? data.data : null;
    this.hoverFeatureSubject.next(data);
    this.changeCurrentFeature(hoverFeature);
  }

  getAvailableStyles(): MapStyle[] {
    return this.availableStyles;
  }

  public getMarker(markerId: string): MapElement<Point> {
    const layers = this.layersBehaviorSubject.getValue()
      .filter(layer => layer.style.layerType === 'html-marker');
    for (const layer of layers) {
      // Cast to MapElement<Point> because html-marker -type layers hold those
      const foundFeature = layer.getSingleFeature(markerId) as MapElement<Point>;
      if (foundFeature) {
        return foundFeature;
      }
    }
  }

  public getFeatureById(featureId: number | string): MapElement<Geometry> {
    const layers = this.layersBehaviorSubject.getValue();
    for (const layer of layers) {
      const foundFeature = layer.getSingleFeature(featureId);
      if (foundFeature) {
        return foundFeature;
      }
    }
    console.log('Feature not found!');
  }

  /**
   * Mapbox wrapper methods
   */

  public setStyle(style: MapStyle): void {
    this.mapState.style = style;
    this.stateService.updateState('mapState', this.mapState);
    this.map.setStyle(style.url);
  }

  public getStyle(): MapStyle {
    return this.mapState.style;
  }

  public setCenter(latLng: { lat: number, lng: number }): void {
    this.map.setCenter(latLng);
  }

  public getCenter(): { lat: number, lng: number } {
    const center = this.map.getCenter();
    return { lat: center.lat, lng: center.lng };
  }

  public getBounds(): MapBounds {
    return this.map.getBounds();
  }

  public restrictBounds(bounds: { sw: MapLatLng, ne: MapLatLng } | null): void {
    if (bounds) {
      this.map.setMaxBounds([bounds.sw, bounds.ne]);
    } else {
      this.map.setMaxBounds(null);
    }
  }

  public getMap() {
    return this.map;
  }

  public getAccessToken() {
    return this.accessToken;
  }

  public zoomIn() {
    this.map.zoomIn();
  }

  public zoomOut() {
    this.map.zoomOut();
  }
  public zoomTo(level: number) {
    this.map.zoomTo(level);
  }
  public getZoom() {
    return this.map.getZoom();
  }

  public flyTo(center: { lat: number, lng: number }, zoom?: number) {
    this.map.flyTo({ center, zoom: zoom || this.getZoom() });
  }

  public getBearing() {
    return this.map.getBearing();
  }

  public searchResults(query: string, language = 'en'): Promise<MapPlace[]> {
    const center = this.getCenter();
    const config = {
      query: query,
      countries: ['fi'],
      types: ['place', 'address', 'poi'],
      language: [language],
      proximity: this.getZoom() > 10 ? [center.lng, center.lat] : undefined
    };
    return this.geocoder.forwardGeocode(config)
      .send()
      .then(res => {
        const features = res.body.features;
        return features.map(feature => ({ description: feature.place_name, geometry: feature.geometry }));
      });
  }

  public async showPlaceOnMap(place: MapPlace) {
    const coordinates = (place.geometry as Point).coordinates as mapboxgl.LngLatLike;
    const latLng = { lng: coordinates[0], lat: coordinates[1] };
    const marker = this.createNewMarker(latLng);
    const popup = new mapboxgl.Popup({ offset: 35 })
      .setText(place.description)
      .setLngLat(coordinates)
      .addTo(this.map);

    // Add event listener outside angular zone
    this.ngZone.runOutsideAngular(() => {
      popup.on('close', () => marker.remove());
    });

    this.flyTo(latLng, 13);
  }

  public showVivoAssetOnMap(id: string): void {
    // Subscribe to visibleMarkers$ until the wanted feature is found
    const sub = this.allMarkers$.subscribe(markers => {
      const feature = markers.find(feature => feature.id.toString() === id);
      if (feature !== undefined) {
        feature.layer.setVisibility(true, true);
        this.flyTo({
          lat: feature.geometry.coordinates[1],
          lng: feature.geometry.coordinates[0]
        }, 8);
        sub.unsubscribe();
      }
    });
  }

  public setMarkerMode(mode: MarkerMode) {
    this.mapState = {
      ...this.mapState,
      markerMode: mode
    };
    this.stateService.updateState('mapState', this.mapState);
    location.reload();
    this.markerModeBS.next(mode);
  }

  public getHasMultipleMarkerModes() {
    return this.hasMultipleMarkerModes;
  }
}
